import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import './FeedbackModal.css';
import apiService from '../services/api';
import { User } from '../types';

interface FeedbackModalProps {
  onClose: () => void;
  user?: User | null;
}

interface FeedbackFormData {
  type: 'bug' | 'feature';
  description: string;
  screenshot: string | null;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ onClose, user }) => {
  const [formData, setFormData] = useState<FeedbackFormData>({
    type: 'bug',
    description: '',
    screenshot: null
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const screenshotInputRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            setFormData(prev => ({ ...prev, screenshot: base64String }));
          };
          reader.readAsDataURL(blob);
        }
        e.preventDefault();
        break;
      }
    }
  };

  const handleScreenshotRemove = () => {
    setFormData(prev => ({ ...prev, screenshot: null }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.description.trim()) {
      setError('Please provide a description');
      return;
    }

    // Get contact from user (prefer phone, fallback to email)
    const contact = user?.phone || user?.email || '';
    if (!contact) {
      setError('Unable to find your contact information. Please update your profile.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiService.submitBugReport({
        type: formData.type,
        contact: contact,
        description: formData.description,
        screenshot: formData.screenshot
      });
      
      setSubmitted(true);
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit bug report. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="feedback-modal-overlay" onClick={handleOverlayClick}>
      <div className="feedback-modal">
        {submitted ? (
          <div className="feedback-success">
            <div className="success-icon">✓</div>
            <p>Thank you!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-form">
            <button className="feedback-close-button" onClick={onClose} type="button">
              <X size={18} />
            </button>

            {error && (
              <div className="feedback-error">
                {error}
              </div>
            )}

            <div className="feedback-form-group">
              <label className="feedback-label">Type</label>
              <div className="feedback-toggle">
                <button
                  type="button"
                  className={`feedback-toggle-button ${formData.type === 'bug' ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, type: 'bug' }))}
                >
                  🐛 Bug Report
                </button>
                <button
                  type="button"
                  className={`feedback-toggle-button ${formData.type === 'feature' ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, type: 'feature' }))}
                >
                  ✨ Feature Request
                </button>
              </div>
            </div>

            <div className="feedback-form-group">
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Tell us more..."
                className="feedback-textarea"
                rows={3}
                required
              />
            </div>

            <div className="feedback-form-group">
              <label htmlFor="screenshot" className="feedback-label">
                Screenshot (Optional)
              </label>
              {formData.screenshot ? (
                <div className="feedback-screenshot-preview">
                  <img src={formData.screenshot} alt="Screenshot preview" />
                  <button
                    type="button"
                    onClick={handleScreenshotRemove}
                    className="feedback-screenshot-remove"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div
                  ref={screenshotInputRef}
                  className="feedback-screenshot-input"
                  onPaste={handlePaste}
                  contentEditable
                  suppressContentEditableWarning
                  data-placeholder="Paste screenshot here (Ctrl+V or Cmd+V)"
                />
              )}
            </div>

            <div className="feedback-form-actions">
              <button 
                type="submit" 
                className="feedback-submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default FeedbackModal;

